import React from 'react';
import { convertFromHTML, convertToHTML } from 'draft-convert';
import { CompositeDecorator, RichUtils, EditorState } from 'draft-js';
import { getSelectionEntity, getSelectedBlock } from 'draftjs-utils';
import HTMLLink from './HTMLLink';

const findLinkEntities = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null
        && contentState.getEntity(entityKey).getType() === 'LINK'
      );
    },
    callback,
  );
};

const getDecorator = () => new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: HTMLLink,
  },
]);

const createEmptyEditorState = () => {
  const decorator = getDecorator();
  return EditorState.createEmpty(decorator);
};

const createEditorStateWithContent = (content) => {
  const decorator = getDecorator();
  return EditorState.createWithContent(content, decorator);
};

const setEditorState = (editorState, currentContent) => {
  const decorator = getDecorator();
  return EditorState.set(editorState, {
    currentContent,
    decorator,
  });
};

const convertEditorStateToHtml = (content) => {
  const html = convertToHTML({
    entityToHTML: (entity, originalText) => {
      if (entity.type === 'IMAGE') {
        return (
          <div style={{ textAlign: 'center' }}>
            <img
              style={{
                maxHeight: 150,
                width: '100%',
                objectFit: 'contain',
              }}
              src={entity.data.src}
              alt={entity.data.subtitle}
            />
          </div>
        );
      }
      if (entity.type === 'LINK') {
        return <a href={entity.data.url}>{originalText}</a>;
      }
      return originalText;
    },
    blockToHTML: (block) => {
      const blockType = block.type;
      if (blockType === 'unstyled' || blockType === 'header-one'
        || blockType === 'header-two' || blockType === 'header-three'
        || blockType === 'header-four' || blockType === 'header-five'
        || blockType === 'header-six') {
        const { align } = block.data;
        let className;
        switch (align) {
          case 'align-right':
            className = 'align-right';
            break;
          case 'align-center':
            className = 'align-center';
            break;
          case 'align-justify':
            className = 'align-justify';
            break;
          case 'align-left':
            className = 'align-left';
            break;
          default:
            break;
        }
        switch (blockType) {
          case 'unstyled':
            return <p className={className} />;
          case 'header-one':
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h1 className={className} />;
          case 'header-two':
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h2 className={className} />;
          case 'header-three':
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h3 className={className} />;
          case 'header-four':
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h4 className={className} />;
          case 'header-five':
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h5 className={className} />;
          case 'header-six':
            // eslint-disable-next-line jsx-a11y/heading-has-content
            return <h6 className={className} />;
          default:
            return <p className={className} />;
        }
      }
      return null;
    },
  })(content.getCurrentContent());

  return html;
};

const convertHtmlToEditorState = (content) => {
  const contentState = convertFromHTML({
    htmlToBlock: (nodeName, node) => {
      const classes = node.classList;
      let align;
      if (nodeName === 'figure') {
        return 'atomic';
      } if (nodeName === 'p') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'unstyled',
          data: {
            align,
          },
        };
      } if (nodeName === 'h1') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'header-one',
          data: {
            align,
          },
        };
      } if (nodeName === 'h2') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'header-two',
          data: {
            align,
          },
        };
      } if (nodeName === 'h3') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'header-three',
          data: {
            align,
          },
        };
      } if (nodeName === 'h4') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'header-four',
          data: {
            align,
          },
        };
      } if (nodeName === 'h5') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'header-five',
          data: {
            align,
          },
        };
      } if (nodeName === 'h6') {
        if (classes.length > 0) {
          [align] = classes;
        }
        return {
          type: 'header-six',
          data: {
            align,
          },
        };
      }
      return null;
    },
    htmlToEntity: (nodeName, node, createEntity) => {
      if (nodeName === 'img') {
        const url = node.src;
        const caption = node.alt;
        return createEntity('IMAGE', 'MUTABLE',
          {
            src: url,
            subtitle: caption,
          });
      } if (nodeName === 'a') {
        const url = node.href;
        return createEntity('LINK', 'MUTABLE',
          {
            url,
          });
      }
      return null;
    },
    blockToHTML: {
      atomic: {
        start: '<figure>',
        end: '</figure>',
      },
    },
  })(content);
  return createEditorStateWithContent(contentState);
};

const getEntities = (editorState, entityType = null) => {
  const content = editorState.getCurrentContent();
  const entities = [];
  content.getBlocksAsArray().forEach((block) => {
    let selectedEntity = null;
    block.findEntityRanges(
      (character) => {
        if (character.getEntity() !== null) {
          const entity = content.getEntity(character.getEntity());
          if (!entityType || (entityType && entity.getType() === entityType)) {
            selectedEntity = {
              entityKey: character.getEntity(),
              blockKey: block.getKey(),
              entity: content.getEntity(character.getEntity()),
            };
            return true;
          }
        }
        return false;
      },
      (start, end) => {
        entities.push({ ...selectedEntity, start, end });
      },
    );
  });
  return entities;
};

const getEntity = (editorState, entityType, entityKey) => {
  const entities = getEntities(editorState, entityType);
  let entity = null;
  entities.forEach((el) => {
    if (el.entityKey === entityKey) {
      entity = el.entity.getData();
    }
  });
  return entity;
};

const getCurrentTextAlignment = (editorState) => {
  if (RichUtils.getCurrentBlockType(editorState) === 'atomic') {
    return 'align-center';
  }
  const align = getSelectedBlock(editorState).getData().get('align');
  return align || 'align-left';
};

const isCurrentlySelectingText = (editorState) => {
  const startOffset = editorState.getSelection().getStartOffset();
  const endOffset = editorState.getSelection().getEndOffset();
  return startOffset !== endOffset;
};

const isCurrentlyCursorOnLink = (editorState) => {
  const entityKey = getSelectionEntity(editorState);
  let link = null;
  if (entityKey) {
    link = getEntity(editorState, 'LINK', entityKey);
  }
  return link !== null;
};

const isCurrentlySelectingImage = (editorState) => RichUtils.getCurrentBlockType(editorState) === 'atomic';

const getBlockStyleFn = (block) => {
  const align = block.getData().get('align');
  switch (align) {
    case 'align-right':
      return 'align-right';
    case 'align-center':
      return 'align-center';
    case 'align-justify':
      return 'align-justify';
    case 'align-left':
      return 'align-left';
    default:
      return 'align-left';
  }
};

export {
  createEmptyEditorState,
  createEditorStateWithContent,
  convertEditorStateToHtml,
  convertHtmlToEditorState,
  setEditorState,
  getCurrentTextAlignment,
  isCurrentlySelectingText,
  isCurrentlyCursorOnLink,
  isCurrentlySelectingImage,
  getEntities,
  getEntity,
  getBlockStyleFn,
};
